/**
 * useAdminScope — single source of truth for "what can the current admin see?"
 *
 * Reads the live `/api/auth/me` payload and returns:
 *   - role:           'super_admin' | 'systems_admin' | 'services_admin' | 'academy_admin'
 *   - division:       null | 'systems' | 'services' | 'academy'
 *   - allowedTabs:    Set<string>      // tab IDs this admin may see
 *   - canManageUsers: boolean          // create/list/reset users (super-admin only)
 *   - email:          string
 *   - displayName:    string           // "Super Admin", "Systems Admin", …
 *
 * Hides tab nav buttons + early-returns inside tab bodies based on this scope.
 * Backend already enforces the same rules — this hook keeps the UI honest.
 */
import { useEffect, useState } from 'react';

const SUPER_ADMIN_EMAIL = 'admin@zhelta.com';
// Hit /auth/me directly against the backend root — NOT through the cart-central
// `api` axios instance whose baseURL is `${BACKEND_URL}/api/store` (it would
// produce `/api/store/api/auth/me` and 404, silently dropping the user into
// the legacy super-admin fallback below and leaking ALL tabs to division admins).
const BACKEND_URL = (process.env.REACT_APP_API_BASE_URL || process.env.CRA_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);

const DIVISION_TABS = {
  systems:  new Set(['overview', 'revenue', 'leads', 'discounts']),
  services: new Set(['overview', 'revenue', 'services', 'catalog', 'discounts']),
  academy:  new Set(['overview', 'revenue', 'academy', 'discounts']),
};

const SUPER_TABS = new Set([
  'overview', 'revenue', 'leads', 'services', 'users', 'catalog', 'academy', 'team', 'discounts',
]);

const DIVISION_DISPLAY = {
  systems:  'Systems Admin',
  services: 'Services Admin',
  academy:  'Academy Admin',
};


export function useAdminScope() {
  const [scope, setScope] = useState({
    loading: true, role: null, division: null,
    allowedTabs: new Set(['overview']),
    canManageUsers: false, email: '', displayName: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        const email = (data?.email || '').toLowerCase();
        const div = (data?.division || '').toLowerCase();
        const isSuper = email === SUPER_ADMIN_EMAIL;

        let role, allowedTabs, displayName;
        if (isSuper) {
          role = 'super_admin';
          allowedTabs = SUPER_TABS;
          displayName = 'Super Admin';
        } else if (DIVISION_TABS[div]) {
          role = `${div}_admin`;
          allowedTabs = DIVISION_TABS[div];
          displayName = DIVISION_DISPLAY[div];
        } else {
          // Authenticated as something else (academy student, etc.) — should
          // not be on /admin at all, but render as bare overview so we don't
          // explode. Backend will 403 every API call anyway.
          role = 'unknown';
          allowedTabs = new Set();
          displayName = data?.name || data?.email || 'Unknown';
        }

        setScope({
          loading: false,
          role,
          division: isSuper ? null : (div || null),
          allowedTabs,
          canManageUsers: isSuper,
          email,
          displayName,
        });
      } catch {
        if (!cancelled) {
          // Not signed-in — legacy header-password fallback. Treat as
          // super-admin (legacy gate is the master password) so the older
          // single-password workflow still works for ops.
          setScope({
            loading: false,
            role: 'super_admin',
            division: null,
            allowedTabs: SUPER_TABS,
            canManageUsers: true,
            email: '',
            displayName: 'Admin (legacy gate)',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return scope;
}
